/**
 * Shared photo URL utility functions for Static Web Generator
 * Consolidates duplicated getPhotoUrl functions
 */

import { ImageData, ImageInfo } from "../../../types/staticWebGenerator";

/**
 * GET /images/{salonId} may return thumbnail rows; marketing library UI shows originals only.
 */
export function marketingLibraryImages(
  images: ImageInfo[] | undefined,
): ImageInfo[] {
  return (images ?? []).filter((img) => !img.is_thumbnail);
}

/**
 * Extract photo URL from various image data formats
 * Handles: string, { url }, { src }, { image_url }, { cloudfront_url }
 */
export const getPhotoUrl = (
  photo: ImageData | string | null | undefined,
): string | undefined => {
  if (!photo) return undefined;

  // Handle string directly
  if (typeof photo === "string") {
    const trimmed = photo.trim();
    return trimmed !== "" ? trimmed : undefined;
  }

  // Handle object with various URL properties
  if (typeof photo === "object" && photo !== null) {
    // Try each URL property in order of preference
    const urlProps = ["cloudfront_url", "url", "src", "image_url"] as const;
    for (const prop of urlProps) {
      const url = (photo as any)[prop];
      if (url && typeof url === "string") {
        const trimmed = url.trim();
        if (trimmed !== "") return trimmed;
      }
    }
  }

  return undefined;
};

/**
 * Get hero image URL from state
 * Priority: selectedHeroPhoto → hero.photos[0]
 */
export const getHeroImageUrl = (
  selectedHeroPhoto: ImageData | null | undefined,
  heroPhotos: ImageData[] | null | undefined,
): string | undefined => {
  if (selectedHeroPhoto) {
    return getPhotoUrl(selectedHeroPhoto);
  }
  if (heroPhotos && heroPhotos.length > 0) {
    return getPhotoUrl(heroPhotos[0]);
  }
  return undefined;
};
